import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const Enroll = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const programs = {
    'technical-support': {
      title: 'Technical Support Analyst',
      eligibilityUrl: '/programs/technical-support#eligibility'
    },
    'web-design': {
      title: 'Web Design',
      eligibilityUrl: '/programs/web-design#eligibility'
    },
    'graphic-design': {
      title: 'Graphic Design',
      eligibilityUrl: '/programs/graphic-design#eligibility'
    },
    'system-administration': {
      title: 'System Administration',
      eligibilityUrl: '/programs/system-administration#eligibility'
    },
    'data-analyst': {
      title: 'Data Analyst',
      eligibilityUrl: '/programs/data-analyst#eligibility'
    },
    'web-development-ecommerce': {
      title: 'Web Development (E-commerce Focus)',
      eligibilityUrl: '/programs/web-development-ecommerce#eligibility'
    },
    'application-development': {
      title: 'Application Development',
      eligibilityUrl: '/programs/application-development#eligibility'
    }
  };

  const currentProgram = programs[programId] || programs['technical-support'];

  const [formData, setFormData] = useState({
    email: '',
    mobile_phone: '',
    first_name: '',
    last_name: '',
    preferred_name: '',
    province: '',
    date_of_birth: '',
    legally_authorized: '',
    indigenous: '',
    heard_about: '',
    newsletter: false,
    program: currentProgram.title
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase
        .from('enrollments')
        .insert([formData]);

      if (error) throw error;

      setSubmitted(true);
      
      // Reset form after 3 seconds and redirect
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error:', error);
      setError('There was an error submitting your application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Application Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your interest in our {currentProgram.title} program. 
            We will review your application and contact you within 30-45 minutes via email.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 relative overflow-hidden">
      {/* Decorative Blobs */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500 rounded-full opacity-20 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute top-20 right-10 w-32 h-32 bg-pink-500 rounded-full opacity-20 blur-2xl"></div>
      <div className="absolute bottom-32 left-32 w-48 h-48 bg-blue-600 rounded-full opacity-15 blur-3xl"></div>
      <div className="absolute bottom-10 left-1/4 w-40 h-40 bg-pink-600 rounded-full opacity-15 blur-2xl"></div>
      <div className="absolute top-1/3 right-20 w-56 h-56 bg-blue-400 rounded-full opacity-10 blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/3 w-72 h-72 bg-blue-500 rounded-full opacity-10 blur-3xl"></div>

      <div className="max-w-3xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {currentProgram.title} Application Form
          </h1>
          <p className="text-blue-600 font-medium text-lg">
            Thank you for your interest in Plexgate Training's {currentProgram.title} Program!
          </p>
        </div>

        {/* Instructions Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <p className="text-gray-700 mb-4">
            Please carefully read the following instructions before submitting your application.
          </p>
          
          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6">
            <p className="text-gray-700 mb-2">
              <strong>There is never a fee associated with applying to or joining any Plexgate Training programs.</strong> Plexgate Training does not ask for payment details at any time, before, during, or after the program. Our programs are always provided for free, thanks to our generous funders.
            </p>
          </div>

          <div className="space-y-3 text-gray-700">
            <div className="flex gap-3">
              <span className="font-bold text-blue-600">1.</span>
              <p>
                Applications to our programs are considered based on our eligibility criteria, your past work experiences and education. Before submitting your application, please carefully read the{' '}
                <a href={currentProgram.eligibilityUrl} className="text-blue-600 hover:underline">
                  {currentProgram.title} program eligibility criteria
                </a>{' '}
                to ensure you are eligible for this program.
              </p>
            </div>
            <div className="flex gap-3">
              <span className="font-bold text-blue-600">2.</span>
              <p>
                <strong>Only one program application form can be submitted per applicant.</strong> If you submit more than one application form from the website (for the same or for a different program), any additional form will be deleted. This includes any form submitted with the same or different email, phone number, address, etc.
              </p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700 mb-3">
              Once you have reviewed this criteria, please take 5 minutes to complete the form below. Once you submit this form, we will work to review your responses as quickly as possible. You will receive an email from us within 30-45 minutes of submission outlining next steps.
            </p>
            <p className="font-bold text-gray-900">Please note the following:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mt-2">
              <li>In some provinces, availability may be limited due to significant interest in our programs.</li>
              <li>You will need to disclose information that would be included in a criminal record check as this may be required to be placed as part of our program.</li>
              <li>Please provide your first and last names exactly as they are listed on Government issued IDs.</li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-blue-600 mb-6">Let's get started!</h2>
          
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Mobile Phone */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Mobile Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="mobile_phone"
                value={formData.mobile_phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* First Name */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                First name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Last name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Preferred Name */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                What name do you go by? <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="preferred_name"
                value={formData.preferred_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Province */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                In which province do you live? <span className="text-red-500">*</span>
              </label>
              <select
                name="province"
                value={formData.province}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a province</option>
                <option value="Alberta">Alberta</option>
                <option value="British Columbia">British Columbia</option>
                <option value="Manitoba">Manitoba</option>
                <option value="New Brunswick">New Brunswick</option>
                <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                <option value="Northwest Territories">Northwest Territories</option>
                <option value="Nova Scotia">Nova Scotia</option>
                <option value="Nunavut">Nunavut</option>
                <option value="Ontario">Ontario</option>
                <option value="Prince Edward Island">Prince Edward Island</option>
                <option value="Quebec">Quebec</option>
                <option value="Saskatchewan">Saskatchewan</option>
                <option value="Yukon">Yukon</option>
              </select>
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Date of birth (YYYY/MM/DD) <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Legally Authorized */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Are you legally able to work full time in Canada for any employer? <span className="text-red-500">*</span>
              </label>
              <select
                name="legally_authorized"
                value={formData.legally_authorized}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select an option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            {/* Indigenous */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Do you identify as an Aboriginal/Indigenous person? <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="indigenous"
                    value="Yes"
                    checked={formData.indigenous === 'Yes'}
                    onChange={handleChange}
                    required
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-gray-700">Yes</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="indigenous"
                    value="No"
                    checked={formData.indigenous === 'No'}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-gray-700">No</span>
                </label>
              </div>
            </div>

            {/* How did you hear about us */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                How did you hear about Plexgate Training? <span className="text-red-500">*</span>
              </label>
              <select
                name="heard_about"
                value={formData.heard_about}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select an option</option>
                <option value="Social Media">Social Media (Facebook, Instagram, LinkedIn, etc.)</option>
                <option value="Google Search">Google Search</option>
                <option value="Friend or Family">Friend or Family</option>
                <option value="Employment Agency">Employment Agency</option>
                <option value="School or University">School or University</option>
                <option value="Community Organization">Community Organization</option>
                <option value="Advertisement">Advertisement</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Newsletter */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="newsletter"
                checked={formData.newsletter}
                onChange={handleChange}
                className="mt-1 w-4 h-4 text-blue-600 rounded"
              />
              <label className="text-gray-700">
                Yes, I would like to receive Plexgate Training's newsletter. I may unsubscribe at any time.
              </label>
            </div>

            {/* reCAPTCHA Placeholder */}
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 text-center text-gray-500">
              reCAPTCHA (Add your reCAPTCHA here)
            </div>

            {/* Privacy Policy */}
            <p className="text-sm text-gray-600">
              By clicking Submit, I confirm that I have read and agree with the{' '}
              <a href="/privacy" className="text-blue-600 hover:underline">privacy policy</a>.
            </p>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-4 rounded-lg hover:bg-blue-700 transition-colors font-bold text-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'SUBMITTING...' : 'SUBMIT'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Enroll;